import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { TAB_BAR_OFFSET } from '../../workspace/tabBarOffset';
import { SAVE_FOOTER_OFFSET } from '../../pedigree-sidebar/saveFooterOffset';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Where the bottom banners stand: on the workspace tab strip, and above the
// sidebar's save footer while that footer is showing somewhere they would
// cover it. Both offsets fall back to 0px, so a page with neither loses
// nothing. Exported so the tests pin the same expression.
export const LOCK_BANNER_BOTTOM = `calc(${TAB_BAR_OFFSET} + ${SAVE_FOOTER_OFFSET})`;

const FORCE_DELAY_MS = 10 * 60 * 1000; // 10 minutes
const HOLDER_WARN_MS = 9 * 60 * 1000; // warn holder 1 min before force is available

export default function LockBanner({
  lockState,
  isMine,
  requestStatus,
  onRequestLock,
  onCancelRequest,
  onForceAcquireLock,
  pendingRequest,
  onRespondToRequest,
  pendingPersonRequest,
  onRespondToPersonRequest,
  sidebarOpen,
  sidebarExpand,
  // Is there a canvas under this banner? A PERSON tab renders no chart, so the
  // one piece of this component that is anchored to the canvas working area —
  // the absolutely-positioned "You are editing" chip — has nothing to annotate
  // there and would float across the patient record.
  //
  // It gates the chip ONLY. The request banners below are position:fixed along
  // the bottom of the window and are the holder's sole Grant/Decline controls,
  // so suppressing them on a person tab (which is what rendering this whole
  // component behind `!personMemberId` used to do) left a lock holder unable to
  // answer a colleague at all.
  canvasChrome = true,
  // Horizontal shift (px) applied so the chip centres on the canvas WORKING
  // area (between the left rail and the docked detail panel) rather than the
  // full box. 0 = today's behaviour (centred on the full box). Only non-zero
  // when the stacked save-indicator layout is on, so the flag-off path is
  // byte-identical.
  centerOffset = 0
}) {
  const t = useAutoTranslation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineMessage, setDeclineMessage] = useState('');
  // TGCLOUD-1770 — the decline dialog is now shared between the pedigree-lock
  // request and the person-lock one, so it has to remember which it is for.
  // 'pedigree' | 'person'.
  const [declineTarget, setDeclineTarget] = useState('pedigree');

  // Requester side: allow force-take only after 10 min of waiting
  const [forceAllowed, setForceAllowed] = useState(false);
  const forceTimerRef = useRef(null);

  // Pedigree lock holder warning: 1 min before force-take is available
  const [holderWarning, setHolderWarning] = useState(false);
  const holderWarnTimerRef = useRef(null);

  // Person lock holder warning: 1 min before force-take is available
  const [personHolderWarning, setPersonHolderWarning] = useState(false);
  const personHolderWarnTimerRef = useRef(null);

  useEffect(() => {
    if (requestStatus === 'pending') {
      setForceAllowed(false);
      forceTimerRef.current = setTimeout(
        () => setForceAllowed(true),
        FORCE_DELAY_MS
      );
    } else {
      setForceAllowed(false);
      clearTimeout(forceTimerRef.current);
    }
    return () => clearTimeout(forceTimerRef.current);
  }, [requestStatus]);

  useEffect(() => {
    if (pendingRequest) {
      setHolderWarning(false);
      holderWarnTimerRef.current = setTimeout(
        () => setHolderWarning(true),
        HOLDER_WARN_MS
      );
    } else {
      setHolderWarning(false);
      clearTimeout(holderWarnTimerRef.current);
    }
    return () => clearTimeout(holderWarnTimerRef.current);
  }, [pendingRequest]);

  useEffect(() => {
    if (pendingPersonRequest) {
      setPersonHolderWarning(false);
      personHolderWarnTimerRef.current = setTimeout(
        () => setPersonHolderWarning(true),
        HOLDER_WARN_MS
      );
    } else {
      setPersonHolderWarning(false);
      clearTimeout(personHolderWarnTimerRef.current);
    }
    return () => clearTimeout(personHolderWarnTimerRef.current);
  }, [pendingPersonRequest]);

  if (!lockState && !pendingPersonRequest) return null;

  const openDecline = (target) => {
    setDeclineMessage('');
    setDeclineTarget(target);
    setDeclineOpen(true);
  };

  // TGCLOUD-1770 — the holder's person-lock request banner. It used to be a
  // dismiss-only Alert reading "Deselect the person to release your lock",
  // which meant nothing ever emitted `person:lock-release-response`: the
  // requester's button stayed on "Requesting..." until the 10-minute
  // force-take timer, and "granted"/"declined" were unreachable states.
  //
  // Defined once and rendered from BOTH return paths below — the isMine branch
  // and the non-isMine one — which previously carried near-duplicate copies
  // that had already drifted (only one of them named the person).
  const personRequestAlert = pendingPersonRequest && (
    <Alert
      severity={personHolderWarning ? 'warning' : 'info'}
      sx={{ alignItems: 'center' }}
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={() => onRespondToPersonRequest?.(true)}
          >
            {t('Release Lock')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="info"
            onClick={() => openDecline('person')}
          >
            {t('Decline')}
          </Button>
        </Box>
      }
    >
      <strong>{pendingPersonRequest.requesterName}</strong>{' '}
      {t('is requesting access to edit')}{' '}
      <strong>
        {[
          pendingPersonRequest.personName,
          pendingPersonRequest.personUpn
            ? `UPN ${pendingPersonRequest.personUpn}`
            : null
        ]
          .filter(Boolean)
          .join(' — ') || t('a record')}
      </strong>
      .
      {personHolderWarning &&
        ` ${t(
          'They will be able to force-take your lock in less than 1 minute.'
        )}`}
    </Alert>
  );

  // Shared by both request kinds; `declineTarget` decides who is answered.
  const declineeName =
    declineTarget === 'person'
      ? pendingPersonRequest?.requesterName
      : pendingRequest?.requesterName;

  const declineDialog = (
    <Dialog
      open={declineOpen}
      onClose={() => setDeclineOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>{t('Decline access request')}</DialogTitle>
      {/* TGCLOUD-1711 — see SidebarLockBanner.jsx for why this needs
          !important: MUI's `.MuiDialogTitle-root + &` rule zeroes the top
          padding and outranks a plain sx class. */}
      <DialogContent sx={{ pt: '16px !important' }}>
        <DialogContentText sx={{ mb: 2 }}>
          {t('You can optionally send a message to')}{' '}
          <strong>{declineeName}</strong>{' '}
          {t('explaining why their request was declined.')}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          label={t('Message (optional)')}
          value={declineMessage}
          onChange={(e) => setDeclineMessage(e.target.value)}
          placeholder={t(
            "e.g. I'm still making changes, please try again later."
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeclineOpen(false)} variant="outlined">
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setDeclineOpen(false);
            const msg = declineMessage || undefined;
            if (declineTarget === 'person')
              onRespondToPersonRequest?.(false, msg);
            else onRespondToRequest(false, msg);
          }}
        >
          {t('Decline Request')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Offset right edge so the banner doesn't overlap the open sidebar
  const sidebarOffset = sidebarOpen && !sidebarExpand ? 400 : 0;

  // And the bottom edge sits ON the workspace tab strip, not under it
  // (TGCLOUD-1839). The strip floats over the last 40px of the window and is
  // chrome: it paints above each tab's mount, which is its own stacking
  // context, so this banner's zIndex 1300 only ranks it inside the tab and
  // cannot lift it over the strip. At `bottom: 0` the solid tab chips, laid out
  // from the left, sat over the lower part of each banner's message, which is
  // where it names the colleague asking or holding the lock, and with enough
  // tabs open over its buttons too. The strip publishes its height (0px when
  // no tab is open, or with the workspace flag off), so this is a no-op
  // anywhere the strip is not.
  //
  // And it clears the sidebar's save footer while one is showing where the
  // banner would cover it: the person tab's full-width region, and the
  // promoted drawer, which runs past the banner's 400px right inset. The
  // banner used to sit over Discard and Save there, so a clinician with
  // unsaved edits could not save while a colleague was asking for the lock.
  // The footer publishes its height (saveFooterOffset.js); 0px otherwise,
  // which includes the peek drawer, whose footer the banner already stops
  // short of.
  const bannerBottom = LOCK_BANNER_BOTTOM;

  if (isMine) {
    return (
      <>
        {canvasChrome && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: centerOffset ? `calc(50% - ${centerOffset / 2}px)` : '50%',
              transform: 'translateX(-50%)',
              zIndex: 1300
            }}
          >
            <Chip
              icon={
                <LockIcon sx={{ fontSize: 16, color: '#155724 !important' }} />
              }
              label={t('You are editing')}
              size="small"
              sx={{ bgcolor: '#d4edda', color: '#155724', fontWeight: 'bold' }}
            />
          </Box>
        )}
        {/* Bottom banners for the pedigree lock holder */}
        {(pendingRequest || pendingPersonRequest) && (
          <Box
            sx={{
              position: 'fixed',
              bottom: bannerBottom,
              left: 0,
              right: sidebarOffset,
              zIndex: 1300,
              px: 2,
              pb: 0.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            {pendingRequest && (
              <Alert
                severity={holderWarning ? 'warning' : 'info'}
                sx={{ alignItems: 'center' }}
                action={
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      onClick={() => onRespondToRequest(true)}
                    >
                      {t('Release Lock')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => openDecline('pedigree')}
                    >
                      {t('Decline')}
                    </Button>
                  </Box>
                }
              >
                <strong>{pendingRequest.requesterName}</strong>{' '}
                {t('is requesting edit access to this pedigree.')}
                {holderWarning &&
                  ` ${t(
                    'They will be able to force-take your lock in less than 1 minute.'
                  )}`}
              </Alert>
            )}

            {personRequestAlert}
          </Box>
        )}
        {declineDialog}
      </>
    );
  }

  // Non-isMine: person lock request banner stacks above pedigree lock banner
  const holderName = lockState?.lockedByName || t('Another user');

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: bannerBottom,
          left: 0,
          right: sidebarOffset,
          zIndex: 1300,
          px: 2,
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        {personRequestAlert}

        {lockState && (
          <Alert
            severity="warning"
            icon={<LockIcon fontSize="inherit" />}
            action={
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center'
                }}
              >
                {requestStatus === 'pending' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <CircularProgress size={14} />
                    <span style={{ fontSize: 12 }}>{t('Waiting...')}</span>
                    <Button
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={onCancelRequest}
                      sx={{ fontSize: 12, py: 0, minWidth: 'auto' }}
                    >
                      {t('Cancel')}
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={onRequestLock}
                  >
                    {t('Request Edit Access')}
                  </Button>
                )}
                {forceAllowed && (
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<LockOpenIcon fontSize="small" />}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {t('Force Take Edit Access')}
                  </Button>
                )}
              </Box>
            }
            sx={{ alignItems: 'center' }}
          >
            {t('This pedigree is currently being edited by')}{' '}
            <strong>{holderName}</strong>
            {t('. Editing is disabled.')}
          </Alert>
        )}
      </Box>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('Force Take Edit Access')}</DialogTitle>
        {/* TGCLOUD-1711 — as above. */}
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText>
            {t('Taking edit access will immediately remove')}{' '}
            <strong>{holderName}</strong>
            {t(
              "'s ability to edit this pedigree. Any unsaved changes they have may be lost. Are you sure you want to continue?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmOpen(false);
              onForceAcquireLock();
            }}
          >
            {t('Take Edit Access')}
          </Button>
        </DialogActions>
      </Dialog>
      {declineDialog}
    </>
  );
}
